import type { BlockField } from '@/blocks/domain/types';
import { getDefaultFieldsForDocumentType } from './defaultFieldsRegistry';

const legacyRow = (partial: Pick<BlockField, 'id' | 'sortOrder'> & Partial<BlockField>): BlockField => ({
  label: '',
  value: '',
  width: 'half',
  align: 'left',
  style: 'label_value',
  visible: true,
  ...partial
});

/**
 * The flat Bill to / Job site rows every customer block was seeded with before LLAMS-844 (no sections, no
 * columns, empty Job site rows), kept verbatim for STORED content. A stored `{ kind: 'customer_block' }` that
 * names no fields has always filled in these rows and must keep doing so: it is a copy (copy-on-import), so
 * a later change to what a NEW block starts with must never re-lay it out.
 */
export const LEGACY_QUOTE_CUSTOMER_FIELDS: readonly BlockField[] = [
  legacyRow({ id: 'cust-pf-heading', sortOrder: 0, style: 'heading', value: 'Bill to' }),
  legacyRow({ id: 'cust-pf-name', sortOrder: 1, value: '{{customer.name}}' }),
  legacyRow({ id: 'cust-pf-contact', sortOrder: 2, value: '{{contact.name}}' }),
  legacyRow({ id: 'cust-pf-address', sortOrder: 3, value: '{{customer.address}}' }),
  legacyRow({ id: 'cust-pb-heading', sortOrder: 4, align: 'right', style: 'heading', value: 'Job site' }),
  legacyRow({ id: 'cust-pb-name', sortOrder: 5, align: 'right' }),
  legacyRow({ id: 'cust-pb-address', sortOrder: 6, align: 'right' }),
  legacyRow({ id: 'cust-pb-contact', sortOrder: 7, align: 'right' })
];

/**
 * The customer rows a STORED block with no fields of its own is filled with (the normalisation path: import,
 * library hydrate). Rows a host registered for the document type win, exactly as they always did; the quote
 * fallback resolves to the legacy flat rows, never to the sectioned rows a new block starts with.
 */
export function legacyCustomerFieldsFor(documentTypeKey?: string | null): BlockField[] {
  const current = getDefaultFieldsForDocumentType(documentTypeKey).customer_block;
  const quoteFallback = getDefaultFieldsForDocumentType(null).customer_block;
  const rows = current === quoteFallback ? LEGACY_QUOTE_CUSTOMER_FIELDS : current;
  return rows.map((row) => ({ ...row }));
}
